<template>
  <div class="booking-map-page">
    <div class="header-card">
      <h2>Fleet Map</h2>
      <p>Live locations for your active bookings.</p>
    </div>

    <div v-if="loading" class="state-card">
      <b-spinner style="width: 2rem; height: 2rem;" />
      <span>Loading active bookings…</span>
    </div>

    <div v-else-if="error" class="state-card state-error">
      <span>{{ error }}</span>
      <b-button size="sm" variant="outline-secondary" @click="loadData">Retry</b-button>
    </div>

    <div v-else-if="markers.length === 0" class="state-card">
      <span>No active bookings with GPS data right now. Check back when a trip is underway.</span>
    </div>

    <div v-else>
      <div class="map-meta">{{ markers.length }} vehicle{{ markers.length !== 1 ? 's' : '' }} tracked</div>
      <GmapMap
        ref="mapRef"
        :zoom="zoom"
        :center="center"
        :options="mapStyle"
        style="width: 100%; height: 78vh;"
      >
        <GmapMarker
          v-for="(marker, idx) in markers"
          :key="idx"
          :position="marker.position"
          :title="marker.title"
          :label="marker.label"
          :draggable="false"
          :clickable="true"
          :icon="marker.icon"
          :zIndex="marker.zIndex"
          @mouseover="openInfoWindow(marker)"
          @mouseout="infoWindow.open = false"
        />
        <GmapInfoWindow
          :options="{ pixelOffset: { width: 0, height: -65 } }"
          :position="infoWindow.position"
          :opened="infoWindow.open"
        >
          <div v-html="infoWindow.template"></div>
        </GmapInfoWindow>
      </GmapMap>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { tbn_get_booking_customer_map } from '../networking/tbn_apis'

export default {
  name: 'booking_customer_fleet_map',
  data() {
    return {
      loading: true,
      error: null,
      markers: [],
      center: { lat: 39.9, lng: -97.1 },
      zoom: 5,
      timer: null,
      infoWindow: { position: { lat: 0, lng: 0 }, open: false, template: '' },
      mapStyle: {
        styles: [
          { featureType: 'landscape', stylers: [{ color: '#F3F4F9' }] },
          { featureType: 'water', stylers: [{ color: '#C1CDE4' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#FFFFFF' }] },
          { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#FEF8D6' }] },
        ],
      },
    }
  },
  computed: {
    ...mapState({ user: (state) => state.profile.user }),
  },
  async created() {
    await this.loadData()
    this.timer = setInterval(() => this.loadData(), 10000)
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    async loadData() {
      try {
        this.error = null
        const { value: { data = {} } } = await tbn_get_booking_customer_map(
          this.user.tenant,
          { status: 'active' }
        )
        const results = data.results || []
        this.markers = results.map((r, idx) => ({
          position: { lat: Number(r.lat), lng: Number(r.lng) },
          title: [r.name, r.route_name].filter(Boolean).join(' – '),
          label: { text: [r.name, r.route_name].filter(Boolean).join(' – '), className: 'tracking-label' },
          icon: this.arrowSymbol(),
          zIndex: 1000 + idx,
          template: r.marker_html || `<b>${r.name}</b>${r.driver_name ? '<br>' + r.driver_name : ''}`,
        }))

        if (this.markers.length > 0) {
          this.center = this.markers[0].position
          if (this.markers.length > 1) this.$nextTick(() => this.fitMapToMarkers())
          else this.zoom = 12
        }
      } catch (e) {
        this.error = 'Unable to load map data. Please try again.'
      } finally {
        this.loading = false
      }
    },
    openInfoWindow(marker) {
      this.infoWindow.position = marker.position
      this.infoWindow.template = marker.template
      this.infoWindow.open = true
    },
    async fitMapToMarkers() {
      try {
        let attempts = 0
        while (!this.$refs.mapRef && attempts++ < 10) {
          await new Promise(r => setTimeout(r, 300))
        }
        if (!this.$refs.mapRef) return
        const bounds = new google.maps.LatLngBounds()
        this.markers.forEach(m => bounds.extend(m.position))
        this.$refs.mapRef.$mapObject.fitBounds(bounds)
      } catch (e) {
        console.warn('fitMapToMarkers failed:', e)
      }
    },
    arrowSymbol() {
      return {
        path: 'M0,0 L-6,14 L0,10 L6,14 Z',
        scale: 1.5,
        strokeColor: 'white',
        fillColor: '#29C68A',
        fillOpacity: 1,
        rotation: 0,
      }
    },
  },
}
</script>

<style scoped>
.booking-map-page { margin: 16px; }
.header-card { background: #f8f9fb; border: 1px solid #d9dce3; border-radius: 8px; padding: 12px; margin-bottom: 12px; }
.header-card h2 { margin: 0; }
.header-card p { margin: 4px 0 0; color: #566; }
.state-card { background: #f8f9fb; border: 1px solid #d9dce3; border-radius: 8px; padding: 32px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 12px; color: #5b6a79; font-size: 14px; }
.state-error { color: #842029; background: #f8d7da; border-color: #f5c2c7; }
.map-meta { font-size: 13px; color: #5b6a79; margin-bottom: 6px; }
</style>
